import { Target, Users, TrendingUp, Award, Lightbulb, Heart } from "lucide-react";

export default function GoalsAndImpact({ userRole }) {
  const priorities = [
    {
      icon: Users,
      title: "Expand Accessibility & Reach",
      description: "We are committed to bringing financial guidance to communities that need it most, including women, single parents, and first-time entrepreneurs, ensuring no one is left behind.",
      image: "https://images.unsplash.com/photo-1565688527174-775059ac429c?w=600&h=800&fit=crop"
    },
    {
      icon: Heart,
      title: "Foster a Thriving Community",
      description: "Anchor Lane is more than a service provider. We are building a supportive ecosystem where clients, mentors, and partners share knowledge, collaborate, and grow together.",
      image: "https://images.unsplash.com/flagged/photo-1551135049-83f3419ef05c?w=600&h=800&fit=crop"
    },
    {
      icon: Target,
      title: "Deliver Personalized Solutions",
      description: "No two journeys are alike. We design strategies that adapt to individual needs — whether that's managing debt, saving for emergencies, or scaling a small business.",
      image: "https://images.unsplash.com/photo-1622675363311-3e1904dc1885?w=600&h=800&fit=crop"
    },
    {
      icon: Award,
      title: "Promote Financial Independence",
      description: "Our ultimate goal is for clients to stand confidently on their own. From creating sustainable budgets to securing long-term investments, we help transform uncertainty into independence.",
      image: "https://images.unsplash.com/photo-1758691737587-7630b4d31d16?w=600&h=800&fit=crop"
    },
    {
      icon: Lightbulb,
      title: "Drive Innovation in Services",
      description: "We stay ahead of financial and business trends, bringing forward-thinking solutions that prepare clients for today's challenges and tomorrow's opportunities.",
      image: "https://images.unsplash.com/photo-1575671883877-71e6519c7570?w=600&h=800&fit=crop"
    }
  ];

  return (
    <div className="min-h-full">
      {/* Hero Section */}
      <div className="relative bg-[#1B3A5F] text-white overflow-hidden" data-testid="goals-hero-section">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1551135049-8a33b5883817?w=1600&h=800&fit=crop"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24 lg:px-8">
          <div className="text-center">
            <h1 className="heading-font text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
              Goals & Impact
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 mb-4 max-w-3xl mx-auto">
              Building a Better Future
            </p>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              At Anchor Lane, we believe true progress happens when financial knowledge turns into 
              measurable results. Our goals aren't just aspirations — they are the roadmap to 
              empowering individuals, families, and entrepreneurs to take control of their futures.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16 lg:px-8 space-y-16">
        {/* Our Key Priorities */}
        <section data-testid="priorities-section">
          <div className="text-center mb-12">
            <h2 className="heading-font text-4xl lg:text-5xl font-bold text-[#1B3A5F] mb-4">
              Our Key Priorities
            </h2>
            <div className="w-20 h-1 bg-[#C5A059] mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Strategic initiatives that drive meaningful change in the communities we serve
            </p>
          </div>

          <div className="space-y-12">
            {priorities.map((priority, index) => {
              const Icon = priority.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={index}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                    !isEven ? "lg:flex-row-reverse" : ""
                  }`}
                  data-testid={`priority-card-${index}`}
                >
                  <div className={`${!isEven ? "lg:order-2" : ""}`}>
                    <img
                      src={priority.image}
                      alt={priority.title}
                      className="rounded-lg shadow-lg w-full h-[400px] object-cover"
                    />
                  </div>
                  
                  <div className={`${!isEven ? "lg:order-1" : ""}`}>
                    <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200">
                      <div className="flex items-center mb-6">
                        <div className="p-4 bg-[#1B3A5F]/10 rounded-full mr-4">
                          <Icon className="h-8 w-8 text-[#1B3A5F]" />
                        </div>
                        <h3 className="heading-font text-2xl font-bold text-[#1B3A5F]">
                          {priority.title}
                        </h3>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">
                        {priority.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Impact Statement */}
        <section className="bg-gradient-to-br from-[#1B3A5F] to-[#2C5282] text-white rounded-lg shadow-xl p-8 lg:p-16" data-testid="impact-statement">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/20 backdrop-blur-md rounded-full">
                <TrendingUp className="h-12 w-12 text-white" />
              </div>
            </div>
            <h2 className="heading-font text-3xl lg:text-4xl font-bold mb-6">
              Creating Lasting Change
            </h2>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              Our commitment extends beyond individual consultations. We're building a movement 
              where financial literacy becomes a catalyst for community transformation, where 
              knowledge shared becomes prosperity multiplied, and where every client's success 
              story inspires countless others to begin their own journey toward financial independence.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-6">
                <div className="text-4xl font-bold text-[#C5A059] mb-2">100%</div>
                <p className="text-white/80">Commitment to Client Success</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-6">
                <div className="text-4xl font-bold text-[#C5A059] mb-2">∞</div>
                <p className="text-white/80">Community Impact Potential</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-6">
                <div className="text-4xl font-bold text-[#C5A059] mb-2">1-on-1</div>
                <p className="text-white/80">Personalized Approach</p>
              </div>
            </div>
          </div>
        </section>

        {/* Join Our Mission */}
        <section className="text-center" data-testid="join-mission">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 lg:p-12">
            <h2 className="heading-font text-3xl lg:text-4xl font-bold text-[#1B3A5F] mb-6">
              Join Us in Building a Better Future
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-8 max-w-3xl mx-auto">
              Whether you're seeking financial guidance, looking to expand your business, or 
              wanting to contribute to a community that values financial literacy — there's a 
              place for you at Anchor Lane. Together, we can transform financial uncertainty 
              into lasting confidence and opportunity.
            </p>
            <a
              href="https://anchorllane.wixsite.com/anchor-lane/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-[#1B3A5F] hover:bg-[#2C5282] text-white px-8 py-4 rounded-md font-medium shadow-sm transition-colors"
              data-testid="contact-us-btn"
            >
              Get in Touch With Us
            </a>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-[#1B3A5F] text-white py-6 mt-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-white/80 text-sm">© 2026 Anchor Lane</p>
        </div>
      </footer>
    </div>
  );
}
